import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5018/api'

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor for auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage or cookie
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
            refreshToken,
          })

          const { accessToken, refreshToken: newRefreshToken } = response.data

          if (typeof window !== 'undefined') {
            localStorage.setItem('access_token', accessToken)
            localStorage.setItem('refresh_token', newRefreshToken)
          }

          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return apiClient(originalRequest)
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login (if not already there)
          if (typeof window !== 'undefined') {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user')
            // Only redirect if not already on login page to avoid infinite loop
            if (!window.location.pathname.startsWith('/login')) {
              window.location.href = '/login'
            }
          }
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, clear any stale tokens and redirect to login (if not already there)
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          localStorage.removeItem('user')
          // Only redirect if not already on login page to avoid infinite loop
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// Type-safe API methods
export const api = {
  // Generic request methods
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.get<T>(url, config),
    
  post: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.post<T>(url, data, config),
    
  put: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.put<T>(url, data, config),
    
  patch: <T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.patch<T>(url, data, config),
    
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => 
    apiClient.delete<T>(url, config),
}

// Error handling utility
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with an error - prefer detail over title for more context
      return error.response.data?.detail || error.response.data?.message || error.response.data?.title || 'An error occurred'
    } else if (error.request) {
      // Request was made but no response
      return 'No response from server. Please check your connection.'
    }
  }
  return (error as Error).message || 'An unexpected error occurred'
}

export default apiClient
/**
 * Origin of the API server (scheme + host), derived from the configured API base URL.
 * Example: "https://api.templatean.com/api" -> "https://api.templatean.com".
 */
export const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL, window.location.origin).origin
  } catch {
    return ''
  }
})()

/**
 * Resolves a file URL returned by the API for use in <img>/<a> tags.
 * The API stores server-relative paths such as "/api/files/{tenant}/{file}"; those must be
 * loaded from the API origin, not the admin app origin. Absolute and data URLs pass through.
 */
export function resolveApiFileUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  if (/^(https?:|data:|blob:)/i.test(url)) return url
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`
  return url
}
